import React, { useState, useEffect } from 'react';
import { Card, Spinner, Badge, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { API_URL } from "../../utils/constant";
import { faSearch, faExclamationTriangle, faExternalLinkAlt, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import Pagination from "../Pagination/Pagination";

// Add styles
const styles = `
    .description-container {
        position: relative;
    }
    
    .description-container .collapsed {
        max-height: 3em;
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
    }
    
    .description-container .expanded {
        max-height: none;
    }
    
    .description-container button {
        font-size: 0.875rem;
        margin-top: 0.25rem;
    }
    
    .description-container button:hover {
        text-decoration: none;
    }
    
    .table-custom td {
        vertical-align: top;
    }
`;

const CveSearch = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [cves, setCves] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [totalItems, setTotalItems] = useState(0);
    const [expandedDescriptions, setExpandedDescriptions] = useState({});

    const fetchCVEs = async () => {
        setLoading(true);
        try {
            const response = await fetch(
                `${API_URL}/cves?page=${currentPage}&limit=${itemsPerPage}&search=${encodeURIComponent(searchTerm)}`,
                {
                    headers: {
                        'Authorization': `Bearer ${localStorage.getItem('session')}`,
                        'Content-Type': 'application/json'
                    }
                }
            );
            const data = await response.json();

            if (data.error) {
                throw new Error(data.userMessage || 'Failed to fetch CVEs');
            }

            setCves(data.data.cves || []);
            setTotalItems(data.data.pagination?.totalItems || 0);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const debounceTimer = setTimeout(() => {
            fetchCVEs();
        }, 500);

        return () => clearTimeout(debounceTimer);
    }, [currentPage, itemsPerPage, searchTerm]);

    const getSeverityColor = (severity) => {
        switch (severity?.toLowerCase()) {
            case 'critical': return 'danger';
            case 'high': return 'warning';
            case 'medium': return 'info';
            case 'low': return 'secondary';
            default: return 'light';
        }
    };

    // Function to get severity based on priority: baseMetricV40 -> baseMetricV3_1 -> baseMetricV3 -> baseMetricV2
    const getSeverity = (impact) => {
        if (!impact) return 'N/A';
        
        // Check baseMetricV40 first
        if (impact.baseMetricV40?.cvssV40?.baseSeverity) {
            return impact.baseMetricV40.cvssV40.baseSeverity;
        }
        
        // Check baseMetricV3_1 second
        if (impact.baseMetricV3_1?.cvssV3_1?.baseSeverity) {
            return impact.baseMetricV3_1.cvssV3_1.baseSeverity;
        }
        
        // Check baseMetricV3 third
        if (impact.baseMetricV3?.cvssV3?.baseSeverity) {
            return impact.baseMetricV3.cvssV3.baseSeverity;
        }
        
        // Check baseMetricV2 last
        if (impact.baseMetricV2?.cvssV2?.baseSeverity) {
            return impact.baseMetricV2.cvssV2.baseSeverity;
        }
        
        return 'N/A';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const toggleDescription = (cveId) => {
        setExpandedDescriptions(prev => ({
            ...prev,
            [cveId]: !prev[cveId]
        }));
    };

    const truncateDescription = (description, maxLength = 150) => {
        if (!description) return '';
        if (description.length <= maxLength) return description;
        return description.substring(0, maxLength) + '...';
    };

    return (
        <div className="container-fluid">
            <div className="content-wrapper">
                <div className="block-header">
                    <h2 className="welcome-title">CVE Search</h2>
                    <p className="welcome-textSmall">Search and browse Common Vulnerabilities and Exposures</p>
                </div>

                {error && (
                    <Alert variant="danger">{error}</Alert>
                )}

                <div className="search-section mb-4">
                    <div className="input-group justify-content-center align-items-center">
                        <div className="searchBoxInput p-0 w-50">
                            <div className="input-group">
                                <span className="input-group-text">
                                    <FontAwesomeIcon icon={faSearch} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search CVEs by ID, description, vendor, or product..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center my-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <Card>
                        <Card.Header className='d-flex align-items-center justify-content-center'>
                            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                            <h4 className='welcome-textSmall mb-0'>CVE Results</h4>
                        </Card.Header>
                        <Card.Body>
                            <div className='table-responsive'>
                                <table className="table table-custom spacing5 mb-0">
                                    <thead>
                                        <tr>
                                            <th style={{ width: '10%' }}>CVE ID</th>
                                            <th style={{ width: '40%' }}>Description</th>
                                            <th style={{ width: '10%' }}>Severity</th>
                                            <th style={{ width: '15%' }}>Published Date</th>
                                            <th style={{ width: '25%' }}>References</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cves?.map((cve) => (
                                            <tr key={cve.id}>
                                                <td>
                                                    <a 
                                                        href={`https://nvd.nist.gov/vuln/detail/${cve.cveId}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        {cve.cveId}
                                                        <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-2" />
                                                    </a>
                                                </td>
                                                <td>
                                                    <div className="description-container">
                                                        <div className={expandedDescriptions[cve.id] ? 'expanded' : 'collapsed'}>
                                                            {expandedDescriptions[cve.id] 
                                                                ? cve.cveDescription 
                                                                : truncateDescription(cve.cveDescription)}
                                                        </div>
                                                        {cve.cveDescription?.length > 150 && (
                                                            <button
                                                                className="btn btn-link p-0 text-primary"
                                                                onClick={() => toggleDescription(cve.id)}
                                                            >
                                                                {expandedDescriptions[cve.id] ? (
                                                                    <>
                                                                        Show Less <FontAwesomeIcon icon={faChevronUp} />
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        Show More <FontAwesomeIcon icon={faChevronDown} />
                                                                    </>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge bg={getSeverityColor(getSeverity(cve.impact))}>
                                                        {getSeverity(cve.impact)}
                                                    </Badge>
                                                </td>
                                                <td>{formatDate(cve.publishedDate)}</td>
                                                <td>
                                                    {cve.references?.slice(0, 2).map((ref, idx) => (
                                                        <div key={idx}>
                                                            <a 
                                                                href={ref.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                            >
                                                                {ref.name || 'Reference'}
                                                                <FontAwesomeIcon icon={faExternalLinkAlt} className="ms-2" />
                                                            </a>
                                                        </div>
                                                    ))}
                                                    {cve.references?.length > 2 && (
                                                        <span className="text-muted">+{cve.references.length - 2} more</span>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="d-flex justify-content-between align-items-center mt-3">
                                <div className="dataTables_paginate">
                                    {totalItems > 0 && (
                                        <Pagination
                                            className="pagination-bar"
                                            currentPage={currentPage}
                                            totalCount={totalItems}
                                            pageSize={itemsPerPage}
                                            onPageChange={(page) => {
                                                setCurrentPage(page);
                                            }}
                                        />
                                    )}
                                </div>
                                {totalItems > itemsPerPage && (
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="form-control limitSelectBox"
                                    >
                                        <option value={20}>20 per page</option>
                                        <option value={50}>50 per page</option>
                                        <option value={100}>100 per page</option>
                                    </select>
                                )}
                            </div>
                        </Card.Body>
                    </Card>
                )}
            </div>
        </div>
    );
};

// Add styles to document
const styleSheet = document.createElement("style");
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default CveSearch; 